import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUniqueId } from "../../../util/hooks";
import PLabel from "./PLabel";
import { classNames } from "../../../util/strings";
import PErrors from "./PErrors";

export type PInputProps = React.ComponentPropsWithoutRef<"input"> & {
  label?: string | null;
  pre?: string;
  errors?: string[];
  onEnterPressed?: (() => void) | null;
  icon?: ReactNode;
  inputClassName?: string;
};

const PInputComponent = forwardRef<HTMLInputElement, PInputProps>(
  (
    {
      label,
      required,
      pre,
      onEnterPressed,
      errors: errorsProp,
      className,
      icon,
      maxLength,
      value,
      inputClassName,
      ...props
    },
    forwardedRef,
  ) => {
    const labelId = useUniqueId();
    const internalRef = useRef<HTMLInputElement>(null);
    const hadFocusWhenDisabledRef = useRef<boolean>(false);

    // Merge forwarded ref with internal ref
    const ref =
      (forwardedRef as React.RefObject<HTMLInputElement>) || internalRef;

    const [text, setText] = useState(value || "");

    useEffect(() => {
      setText(value || "");
    }, [value]);

    // Restore focus when input becomes enabled again if it had focus before
    useEffect(() => {
      if (props.disabled) {
        hadFocusWhenDisabledRef.current =
          document.activeElement === ref.current;
      } else if (hadFocusWhenDisabledRef.current) {
        ref.current?.focus();
        hadFocusWhenDisabledRef.current = false;
      }
    }, [props.disabled, ref]);

    const getTextLength = (): number => {
      if (typeof text !== "string") {
        return 0;
      }
      return text.length;
    };

    const isTextLimitHit = (): boolean => {
      if (!maxLength) {
        return false;
      }
      if (maxLength - getTextLength() > 0) {
        return false;
      }
      return true;
    };

    const getErrors = (): string[] => {
      const errors = [];
      if (errorsProp) {
        errors.push(...errorsProp);
      }
      if (isTextLimitHit()) {
        errors.push("character limit reached");
      }
      return errors;
    };

    const errors = getErrors();
    const inError = Boolean(errors && errors.length > 0);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && onEnterPressed) {
        onEnterPressed();
      }
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    return (
      <div className={className}>
        {label ? (
          <PLabel
            label={label}
            htmlFor={labelId}
            required={required}
            error={inError}
          />
        ) : null}
        <div className="relative shadow-sm">
          {pre && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="sm:text-sm">{pre}</span>
            </div>
          )}
          {icon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              {icon}
            </div>
          )}
          <input
            type="text"
            id={labelId}
            className={classNames(
              "block w-full !border-0 !bg-white p-1.5 ring-1 ring-inset placeholder:text-black/50 !text-black focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 rounded-lg",
              pre ? "pl-7" : null,
              icon ? "pl-10" : null,
              inError ? "ring-danger" : "ring-p-black-lighter",
              props.disabled ? "cursor-not-allowed" : null,
              inputClassName,
            )}
            ref={ref}
            onKeyDown={handleKeyDown}
            required={required}
            value={value}
            maxLength={maxLength}
            onChange={(event) => {
              setText(event.target.value);
              if (props.onChange) props.onChange(event);
            }}
            {...props}
          />
        </div>
        <PErrors errors={errors} />
      </div>
    );
  },
);

PInputComponent.defaultProps = {
  label: null,
  pre: undefined,
  errors: undefined,
  onEnterPressed: null,
  icon: null,
  inputClassName: undefined,
};

export default PInputComponent;
